import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { styled } from '@stitches/react';

// 账户重定向页面样式
const RedirectContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  padding: '2rem',
  textAlign: 'center',
});

const RedirectTitle = styled('h1', {
  fontSize: '2rem',
  marginBottom: '1.5rem',
  fontWeight: 600,
});

const RedirectMessage = styled('p', {
  fontSize: '1.2rem',
  color: '$textSecondary',
  marginBottom: '2rem',
  maxWidth: '600px',
});

const LoadingSpinner = styled('div', {
  width: '50px',
  height: '50px',
  border: '5px solid $backgroundSecondary',
  borderTopColor: '$primary',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  marginBottom: '2rem',
  '@keyframes spin': {
    to: { transform: 'rotate(360deg)' }
  }
});

const ButtonContainer = styled('div', {
  display: 'flex',
  gap: '1rem',
  marginTop: '1rem',
});

const Button = styled('button', {
  padding: '0.75rem 1.5rem',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
  
  variants: {
    variant: {
      primary: {
        backgroundColor: '$primary',
        color: 'white',
        border: 'none',
        
        '&:hover': {
          backgroundColor: '$primaryDark',
        }
      },
      secondary: {
        backgroundColor: 'transparent',
        color: '$textPrimary',
        border: '1px solid $borderLight',
        
        '&:hover': {
          backgroundColor: '$backgroundSecondary',
        }
      }
    }
  },
  
  defaultVariants: {
    variant: 'primary'
  }
});

/**
 * H5 App配置
 * 不同功能对应的h5-app链接配置
 */
const H5_APP_URLS = {
  // 主应用URL
  BASE_URL: 'https://app.BITENGINE.com',
  
  // 账户相关链接
  DASHBOARD: '/dashboard',
  LOGIN: '/login',
  REGISTER: '/register',
  PROFILE: '/profile',
  
  // 云挖矿相关链接
  CLOUD_MINING: '/mining/cloud',
  CLOUD_CONTRACTS: '/mining/cloud/contracts',
  
  // NFT相关链接
  NFT_MARKETPLACE: '/nft/marketplace',
  NFT_MINING: '/nft/mining',
  
  // 钱包相关链接
  WALLET: '/wallet',
  WALLET_DEPOSIT: '/wallet/deposit',
  WALLET_WITHDRAW: '/wallet/withdraw',
  
  // 矿机托管相关链接
  HOSTING: '/hosting',
  HOSTING_ORDERS: '/hosting/orders',
  
  // ASIC商店链接
  ASIC_SHOP: '/shop/asic',
  ASIC_ORDERS: '/shop/orders',
  
  // 其他功能链接
  REFERRALS: '/referrals',
  SUPPORT: '/support',
};

/**
 * 账户重定向组件
 * 处理到H5应用的重定向，支持各种功能页面的跳转
 */
const AccountRedirect: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [countDown, setCountDown] = useState(5);
  const [isRedirecting, setIsRedirecting] = useState(true);
  
  // 获取重定向目标
  const getRedirectTarget = () => {
    // 从URL参数获取目标页面
    const searchParams = new URLSearchParams(location.search);
    const target = searchParams.get('target') || 'dashboard';
    
    // 映射目标页面到h5 app URL
    switch (target.toLowerCase()) {
      case 'login':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.LOGIN;
      case 'register':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.REGISTER;
      case 'profile':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.PROFILE;
      case 'cloud-mining':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.CLOUD_MINING;
      case 'cloud-contracts':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.CLOUD_CONTRACTS;
      case 'nft-marketplace':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.NFT_MARKETPLACE;
      case 'nft-mining':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.NFT_MINING;
      case 'wallet':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.WALLET;
      case 'wallet-deposit':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.WALLET_DEPOSIT;
      case 'wallet-withdraw':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.WALLET_WITHDRAW;
      case 'hosting':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.HOSTING;
      case 'hosting-orders':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.HOSTING_ORDERS;
      case 'asic-shop':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.ASIC_SHOP;
      case 'asic-orders':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.ASIC_ORDERS;
      case 'referrals':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.REFERRALS;
      case 'support':
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.SUPPORT;
      case 'dashboard':
      default:
        return H5_APP_URLS.BASE_URL + H5_APP_URLS.DASHBOARD;
    }
  };
  
  // 目标URL
  const redirectUrl = getRedirectTarget();
  
  // 处理重定向
  useEffect(() => {
    if (!isRedirecting) return;
    
    const timer = setInterval(() => {
      setCountDown((prev) => prev - 1);
    }, 1000);
    
    // 倒计时结束后重定向
    if (countDown <= 0) {
      window.location.href = redirectUrl;
      clearInterval(timer);
    }
    
    return () => clearInterval(timer);
  }, [countDown, redirectUrl, isRedirecting]);
  
  // 取消重定向
  const handleCancel = () => {
    setIsRedirecting(false);
    navigate(-1); // 返回上一页
  };
  
  // 立即前往
  const handleGoNow = () => {
    window.location.href = redirectUrl;
  };
  
  return (
    <RedirectContainer>
      <RedirectTitle>{t('account.redirect.title')}</RedirectTitle>
      
      {isRedirecting && (
        <>
          <LoadingSpinner />
          <RedirectMessage>
            {t('account.redirect.message', { count: countDown, url: redirectUrl })}
          </RedirectMessage>
        </>
      )}
      
      <ButtonContainer>
        <Button onClick={handleGoNow} variant="primary">
          {t('account.redirect.goNow')}
        </Button>
        <Button onClick={handleCancel} variant="secondary">
          {t('account.redirect.cancel')}
        </Button>
      </ButtonContainer>
    </RedirectContainer>
  );
};

export default AccountRedirect; 